import type { CompositionV3Ent } from '@revelio/data-access';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../../../../../shared/selection-lists/types';
import {
  type Filter,
  FilterType,
  type PrimaryFilterId,
  type TreeSelection,
} from '../../types/filters';
import { getNumericIdFromNestedId } from '../get-numeric-id-from-nested-id';
import {
  getEntKindForActivityLevelId,
  getEntKindForGeoOrRicsLevelId,
  getEntKindForRoleLevelId,
  getEntKindForSkillLevelId,
} from '../role-skill-ent-kind';

const resolveCompositionV3EntForSelection = (
  selection: TreeSelection
): CompositionV3Ent | null => {
  const levelId = selection.levelId as AllSelectionListsWithSkillTaxonomyKey;

  const entKind =
    getEntKindForGeoOrRicsLevelId(levelId) ??
    getEntKindForRoleLevelId(String(selection.levelId)) ??
    getEntKindForSkillLevelId(String(selection.levelId)) ??
    getEntKindForActivityLevelId(String(selection.levelId));

  if (!entKind) return null;

  const id = getNumericIdFromNestedId(String(selection.id));
  if (id === null) return null;

  return { EntType: entKind, EntID: id };
};

export const compositionV3EntityKey = (ent: {
  EntType: CompositionV3Ent['EntType'];
  EntID: CompositionV3Ent['EntID'];
}): string => `${ent.EntType}:${ent.EntID}`;

export const getCompositionV3EntityNamesByKey = ({
  primaryDimension,
  effectiveFilters,
}: {
  primaryDimension: PrimaryFilterId;
  effectiveFilters: Filter[];
}): Map<string, string> => {
  const primaryFilter = effectiveFilters.find(
    (filter) => filter.id === primaryDimension
  );

  if (!primaryFilter || primaryFilter.type !== FilterType.TREE) {
    return new Map();
  }

  return primaryFilter.selections.reduce((acc, selection) => {
    const ent = resolveCompositionV3EntForSelection(selection);
    if (ent !== null) {
      const key = compositionV3EntityKey(ent);
      if (!acc.has(key)) {
        acc.set(key, selection.name);
      }
    }
    return acc;
  }, new Map<string, string>());
};
